// Kickoff de lancement : ce que Nexus propose et qui diffère encore de la fiche du projet. Nexus envoie l'état complet
// qu'il voit (brief, vision, itérations) ; on ne montre que les écarts, sauf ceux que l'équipe a écartés. Une
// itération nouvelle écartée l'est par son nom : Nexus peut en changer les chiffres, elle ne revient pas.

export const FIELD_LABELS = {
  name: 'Nom', start_date: 'Début', end_date: 'Fin', hours: 'Temps', purchases_eur: 'Achats', ca_eur: 'CA', vision: 'Vision',
};
const NUMBERS = ['hours', 'purchases_eur', 'ca_eur'];

const norm = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
const empty = (value) => value === null || value === undefined || norm(value) === '';
const key = (name) => `new:${norm(name).toLowerCase()}`;

/** Les valeurs d'une itération de la fiche, sous les noms de la proposition. */
export const iterationValues = (iteration) => ({
  name: iteration.name || '',
  start_date: String(iteration.start_date || '').slice(0, 10),
  end_date: String(iteration.end_date || '').slice(0, 10),
  hours: Number(iteration.budget_time_hours ?? iteration.forecast_time_hours) || 0,
  purchases_eur: Number(iteration.budget_purchase_eur ?? iteration.forecast_purchase_eur) || 0,
  ca_eur: Number(iteration.forecast_ca_eur ?? iteration.valeur) || 0,
  vision: iteration.description || '',
});

const differs = (field, proposed, current) => {
  if (empty(proposed)) return false;
  if (NUMBERS.includes(field)) return Math.abs(Number(proposed) - (Number(current) || 0)) >= 0.5;
  return norm(proposed) !== norm(current);
};

export const isDismissed = (draft, entryKey, value) => (draft?.dismissed || [])
  .some((entry) => entry.key === entryKey && (value === undefined || norm(entry.value) === norm(value)));

export function suggestions(draft, project, iterations = []) {
  const proposal = draft?.draft;
  const none = { brief: null, vision: null, updates: [], additions: [], count: 0 };
  if (!proposal || !project) return none;
  const text = (field) => {
    const value = proposal[field];
    return !empty(value) && norm(value) !== norm(project[field]) && !isDismissed(draft, field, value) ? value : null;
  };
  const byId = new Map(iterations.map((iteration) => [iteration.id, iteration]));
  const names = new Set(iterations.map((iteration) => key(iteration.name)));
  const updates = [];
  const additions = [];
  (proposal.iterations || []).forEach((item) => {
    if (item.id && byId.has(item.id)) {
      const current = iterationValues(byId.get(item.id));
      const fields = Object.fromEntries(Object.keys(FIELD_LABELS)
        .filter((field) => differs(field, item[field], current[field])).map((field) => [field, item[field]]));
      if (!Object.keys(fields).length) return;
      const entry = { key: `it-${item.id}`, value: JSON.stringify(fields), id: item.id, iteration: byId.get(item.id), fields, current };
      if (!isDismissed(draft, entry.key, entry.value)) updates.push(entry);
    } else if (!item.id && !empty(item.name)) {
      if (isDismissed(draft, key(item.name)) || names.has(key(item.name))) return; // écartée, ou déjà ajoutée
      additions.push({ key: key(item.name), item });
    }
  });
  const brief = text('brief');
  const vision = text('vision');
  return { brief, vision, updates, additions, count: (brief ? 1 : 0) + (vision ? 1 : 0) + updates.length + additions.length };
}

/** Champs de la proposition -> champs de l'itération (routes /iterations). */
export const toIterationFields = (fields) => {
  const map = { name: 'name', start_date: 'start_date', end_date: 'end_date', hours: 'forecast_time_hours',
    purchases_eur: 'forecast_purchase_eur', ca_eur: 'forecast_ca_eur', vision: 'description' };
  return Object.fromEntries(Object.entries(fields).filter(([field, value]) => map[field] && !empty(value))
    .map(([field, value]) => [map[field], NUMBERS.includes(field) ? Number(value) : value]));
};

/** Dates d'une itération ajoutée sans dates : six semaines à partir du lundi qui suit la dernière itération. */
export const defaultRange = (iterations = [], today = new Date()) => {
  const last = iterations.map((it) => String(it.end_date || '').slice(0, 10)).filter(Boolean).sort().pop();
  const from = last ? new Date(`${last}T12:00:00Z`) : today;
  const start = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + 1));
  start.setUTCDate(start.getUTCDate() + ((8 - start.getUTCDay()) % 7));
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6 * 7 - 3);
  return { start_date: start.toISOString().slice(0, 10), end_date: end.toISOString().slice(0, 10) };
};

